// 📁 src/App.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import Home from "./Home";
import About from "./About";
import CartPage from "./CartPage";
import CheckoutPage from "./CheckoutPage";
import LoginPage from "./LoginPage";
import RegisterPage from "./RegisterPage";
import OrderHistoryPage from "./OrderHistoryPage";
import OrderConfirmationPage from "./OrderConfirmationPage";
import ProductsPage from "./ProductsPage";
import ExportEnquiryPage from "./ExportEnquiryPage";
import Dashboard from "./admin/Dashboard";
import Orders from "./admin/Orders";
import Products from "./admin/Products";
import Enquiries from "./admin/Enquiries";
import Coupons from "./admin/Coupons";
import PostBlog from "./admin/PostBlog";
import AdminLayout from "./admin/AdminLayout";
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./AdminRoute";
import { CartProvider, CartContext } from "./CartContext";
import { CurrencyProvider, CurrencyContext } from "./context/CurrencyContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUser } from "./UserContext";
import UserAvatar from "./components/UserAvatar";
import ContactPage from "./ContactPage";
import ContactEnquiries from "./admin/ContactEnquiries";
import Footer from "./components/Footer";
import PrivacyPolicy from "./PrivacyPolicy";
import ScrollToTop from "./components/ScrollToTop";
import logo from "./assets/logo/the-nutrafox.svg";
import JournalSubscribers from "./admin/JournalSubscribers";

import {
  ShoppingCart,
  Menu,
  X,
  Home as HomeIcon,
  Info,
  Package,
  Globe,
  Phone,
  LogIn,
} from "lucide-react";

function AppContent() {
  const { currency, setCurrency } = useContext(CurrencyContext);
  const { cart } = useContext(CartContext);
  const { user, loading } = useUser();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);

  // close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.hash]);

  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + (item.quantity || 0), 0),
    [cart]
  );

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    if (drawerOpen) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  const navItems = useMemo(
    () => [
      { to: "/", label: "Home", icon: HomeIcon },
      { to: "/about", label: "About Us", icon: Info },
      { to: "/products", label: "Products", icon: Package },
      { to: "/export-enquiry", label: "Export Enquiry", icon: Globe },
      { to: "/contact", label: "Contact Us", icon: Phone },
    ],
    []
  );

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="bg-[#f3f0e4] text-[#105132] min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-[#105132]/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex items-center justify-between">
          {/* ✅ Brand (REPLACED with official logo) */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src={logo}
              alt="The Nutrafox"
              className="h-10 sm:h-12 w-auto"
              loading="eager"
            />
          </Link>

          {/* Desktop nav (ONLY on large screens) */}
          <nav className="hidden lg:flex items-center gap-6 text-[#105132] font-semibold">
            {navItems.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                className={`relative py-2 transition ${
                  isActive(it.to)
                    ? "text-[#105132]"
                    : "text-[#105132]/80 hover:text-[#105132]"
                }`}
              >
                {it.label}
                {isActive(it.to) && (
                  <span className="absolute left-0 right-0 -bottom-1 mx-auto h-[3px] w-8 rounded-full bg-[#c0a230]" />
                )}
              </Link>
            ))}

            {/* Cart */}
            <Link to="/cart" className="relative ml-2">
              <div className="h-10 w-10 rounded-2xl border border-[#105132]/15 flex items-center justify-center hover:bg-[#105132]/5 transition">
                <ShoppingCart className="w-5 h-5" />
              </div>
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Currency */}
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="ml-2 border border-[#105132]/20 rounded-xl px-3 py-2 text-sm bg-white hover:border-[#c0a230] transition"
            >
              <option value="INR">🇮🇳 INR</option>
              <option value="USD">🇺🇸 USD</option>
              <option value="EUR">🇪🇺 EUR</option>
              <option value="GBP">🇬🇧 GBP</option>
              <option value="AED">🇦🇪 AED</option>
            </select>

            {/* Login / Avatar */}
            {!loading &&
              (user ? (
                <UserAvatar />
              ) : (
                <Link
                  to="/login"
                  className="ml-2 text-sm px-5 py-2 rounded-xl border border-[#105132] text-[#105132] hover:bg-[#105132] hover:text-white transition shadow-sm"
                >
                  Login
                </Link>
              ))}
          </nav>

          {/* Mobile / Tablet actions (up to lg) */}
          <div className="lg:hidden flex items-center gap-3">
            {/* Cart icon */}
            <Link to="/cart" className="relative">
              <div className="h-10 w-10 rounded-2xl border border-[#105132]/15 flex items-center justify-center bg-white hover:bg-[#105132]/5 transition">
                <ShoppingCart className="w-5 h-5" />
              </div>
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Menu button */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="h-10 w-10 rounded-2xl border border-[#105132]/15 flex items-center justify-center bg-white hover:bg-[#105132]/5 transition"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <ScrollToTop />

      {/* Premium Drawer */}
      <div
        className={`fixed inset-0 z-[60] transition ${
          drawerOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        {/* Overlay */}
        <div
          onClick={() => setDrawerOpen(false)}
          className={`absolute inset-0 transition ${
            drawerOpen
              ? "bg-black/40 backdrop-blur-[2px] opacity-100"
              : "opacity-0"
          }`}
        />

        {/* Panel */}
        <aside
          className={`absolute right-0 top-0 h-[100dvh] w-[92%] max-w-[380px] bg-white shadow-2xl transition-transform duration-300 flex flex-col ${
            drawerOpen ? "translate-x-0" : "translate-x-full"
          }`}
          aria-hidden={!drawerOpen}
        >
          {/* Header with gradient */}
          <div className="relative p-5 border-b border-[#105132]/10 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#105132] via-[#105132] to-[#236e47] opacity-95" />
            <div className="absolute -top-16 -left-16 h-40 w-40 rounded-full bg-[#c0a230]/25 blur-2xl" />
            <div className="absolute -bottom-16 -right-16 h-40 w-40 rounded-full bg-white/15 blur-2xl" />

            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* ✅ Optional: use logo in drawer too */}
                <div className="h-11 w-11 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center overflow-hidden">
                  <img
                    src={logo}
                    alt="The Nutrafox"
                    className="h-8 w-auto"
                    loading="eager"
                  />
                </div>
                <div>
                  <div className="text-white font-extrabold text-lg leading-tight">
                    Menu
                  </div>
                  <div className="text-white/80 text-xs font-semibold">
                    Navigate The NutraFox
                  </div>
                </div>
              </div>

              <button
                onClick={() => setDrawerOpen(false)}
                className="h-10 w-10 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/15 transition"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Quick action chips */}
            <div className="relative mt-4 flex gap-2">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-white/10 border border-white/15 text-white text-sm font-semibold hover:bg-white/15 transition"
              >
                <Package className="w-4 h-4" />
                Browse
              </Link>

              <Link
                to="/export-enquiry"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-[#c0a230] text-[#105132] text-sm font-extrabold hover:bg-[#e3c75a] transition"
              >
                <Globe className="w-4 h-4" />
                Export
              </Link>
            </div>
          </div>

          {/* Content */}
          <div className="p-5 flex-1 overflow-y-auto overscroll-contain">
            {/* Nav items */}
            <div className="space-y-2">
              {navItems.map((it) => {
                const Icon = it.icon;
                const active = isActive(it.to);
                return (
                  <Link
                    key={it.to}
                    to={it.to}
                    className={`flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border transition ${
                      active
                        ? "bg-[#105132]/5 border-[#c0a230]/60 shadow-sm"
                        : "bg-white border-[#105132]/10 hover:bg-[#105132]/5"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-10 w-10 rounded-2xl flex items-center justify-center border ${
                          active
                            ? "bg-[#105132] text-white border-[#105132]"
                            : "bg-white text-[#105132] border-[#105132]/15"
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="font-bold text-[#105132]">{it.label}</div>
                    </div>

                    <span
                      className={`text-xs font-extrabold px-2 py-1 rounded-full ${
                        active
                          ? "bg-[#c0a230] text-[#105132]"
                          : "bg-[#105132]/5 text-[#105132]/70"
                      }`}
                    >
                      →
                    </span>
                  </Link>
                );
              })}
            </div>

            {/* Divider */}
            <div className="my-5 h-px bg-[#105132]/10" />

            {/* Currency */}
            <div className="rounded-3xl border border-[#105132]/10 bg-[#f8f6ef] p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-extrabold text-[#105132]">
                  Currency
                </div>
                <span className="text-[11px] font-bold text-[#105132]/70">
                  Global pricing
                </span>
              </div>

              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full border border-[#105132]/15 rounded-2xl px-4 py-3 text-sm bg-white hover:border-[#c0a230] transition"
              >
                <option value="INR">🇮🇳 INR</option>
                <option value="USD">🇺🇸 USD</option>
                <option value="EUR">🇪🇺 EUR</option>
                <option value="GBP">🇬🇧 GBP</option>
                <option value="AED">🇦🇪 AED</option>
              </select>
            </div>

            {/* Login / Avatar */}
            <div className="mt-4">
              {!loading &&
                (user ? (
                  <div className="rounded-3xl border border-[#105132]/10 bg-white p-4">
                    <div className="text-sm font-extrabold text-[#105132] mb-2">
                      Account
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-[#105132]/80 font-semibold">
                        You are logged in
                      </div>
                      <UserAvatar />
                    </div>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-[#105132] text-white font-extrabold hover:bg-[#236e47] transition shadow-md"
                  >
                    <LogIn className="w-5 h-5" />
                    Login
                  </Link>
                ))}
            </div>

            {/* ✅ Removed the old small footer from inside Drawer */}
          </div>
        </aside>
      </div>

      {/* Routes */}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/export-enquiry" element={<ExportEnquiryPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route
          path="/my-orders"
          element={
            <PrivateRoute>
              <OrderHistoryPage />
            </PrivateRoute>
          }
        />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        {/* Protected Routes */}
        <Route
          path="/checkout"
          element={
            <PrivateRoute>
              <CheckoutPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/order-confirmation"
          element={
            <PrivateRoute>
              <OrderConfirmationPage />
            </PrivateRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="orders" element={<Orders />} />
          <Route path="products" element={<Products />} />
          <Route path="enquiries" element={<Enquiries />} />
          <Route path="contact-enquiries" element={<ContactEnquiries />} />
          <Route path="coupons" element={<Coupons />} />
          <Route path="post-blog" element={<PostBlog />} />
          <Route path="subscribers" element={<JournalSubscribers />} />
        </Route>
      </Routes>

      {/* ✅ New global Footer */}
      <Footer />

      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
}

export default function App() {
  return (
    <CurrencyProvider>
      <CartProvider>
        <AppContent />
      </CartProvider>
    </CurrencyProvider>
  );
}
